'use client'

import { AlertCircle, Check, File as FileIcon, RefreshCw, Upload, X } from 'lucide-react'
import { type DragEvent, useRef, useState } from 'react'

import { cn } from '../cn'
import { Button } from './button'
import { Progress } from './progress'

export interface FileUploadState {
  progress: number
  status: 'pending' | 'uploading' | 'done' | 'error'
  error?: string
}

export interface FileInputProps {
  value: File[]
  onChange: (files: File[]) => void
  /** Comma-separated MIME or extension list, e.g. "image/*,application/pdf". */
  accept?: string
  /** Allow multiple files. Default: true. */
  multiple?: boolean
  /** Max bytes per file. Default: 25 MB. */
  maxSize?: number
  /** Max total files. Default: 10. */
  maxFiles?: number
  disabled?: boolean
  /** Per-file upload state, keyed by `File.name`. Consumer tracks progress and passes it back. */
  uploadStates?: Map<string, FileUploadState>
  /** Retry callback invoked when the user clicks retry on a failed upload. */
  onRetry?: (file: File) => void
  className?: string
}

const DEFAULT_MAX_SIZE = 25 * 1024 * 1024
const DEFAULT_MAX_FILES = 10

function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function FileInput({
  value,
  onChange,
  accept,
  multiple = true,
  maxSize = DEFAULT_MAX_SIZE,
  maxFiles = DEFAULT_MAX_FILES,
  disabled = false,
  uploadStates,
  onRetry,
  className,
}: FileInputProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragActive, setDragActive] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function addFiles(incoming: FileList | File[]) {
    const next: File[] = multiple ? [...value] : []
    const existingKeys = new Set(next.map((f) => `${f.name}:${f.size}`))
    const rejects: string[] = []

    for (const file of Array.from(incoming)) {
      const key = `${file.name}:${file.size}`
      if (existingKeys.has(key)) {
        continue
      }
      if (file.size > maxSize) {
        rejects.push(`${file.name} is larger than ${formatBytes(maxSize)}`)
        continue
      }
      if (next.length >= maxFiles) {
        rejects.push(`Maximum ${maxFiles} file${maxFiles === 1 ? '' : 's'}`)
        break
      }
      next.push(file)
      existingKeys.add(key)
      if (!multiple) {
        break
      }
    }

    setError(rejects.length > 0 ? (rejects[0] ?? null) : null)
    onChange(next)
  }

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setDragActive(false)
    if (disabled) {
      return
    }
    if (e.dataTransfer.files?.length) {
      addFiles(e.dataTransfer.files)
    }
  }

  function removeFile(file: File) {
    onChange(value.filter((f) => !(f.name === file.name && f.size === file.size)))
    setError(null)
  }

  const hintParts: string[] = []
  if (accept) {
    hintParts.push(accept.replace(/,/g, ', '))
  }
  hintParts.push(`up to ${formatBytes(maxSize)}`)
  if (multiple && maxFiles > 1) {
    hintParts.push(`max ${maxFiles} files`)
  }

  return (
    <div className={cn('space-y-2', className)}>
      <div
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={(e) => {
          if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault()
            inputRef.current?.click()
          }
        }}
        onDragOver={(e) => {
          e.preventDefault()
          if (!disabled) {
            setDragActive(true)
          }
        }}
        onDragLeave={(e) => {
          e.preventDefault()
          setDragActive(false)
        }}
        onDrop={handleDrop}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        className={cn(
          'flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-input bg-transparent px-4 py-6 text-center transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          dragActive && 'border-primary bg-primary/5',
          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:border-primary/60 hover:bg-accent/30',
        )}>
        <Upload className="h-5 w-5 text-muted-foreground" />
        <div className="text-sm">
          <span className="font-medium text-foreground">Click to upload</span>
          <span className="text-muted-foreground"> or drag and drop</span>
        </div>
        <p className="text-xs text-muted-foreground">{hintParts.join(' · ')}</p>
        <input
          ref={inputRef}
          type="file"
          className="sr-only"
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          onChange={(e) => {
            if (e.target.files?.length) {
              addFiles(e.target.files)
            }
            e.target.value = ''
          }}
        />
      </div>

      {error && (
        <p className="flex items-center gap-1 text-xs text-destructive">
          <AlertCircle className="h-3 w-3 shrink-0" />
          {error}
        </p>
      )}

      {value.length > 0 && (
        <ul className="space-y-1.5">
          {value.map((file) => {
            const state = uploadStates?.get(file.name)
            const status = state?.status
            const progress = state?.progress ?? 0
            return (
              <li
                key={`${file.name}:${file.size}`}
                className="flex items-center gap-3 rounded-md border border-border bg-card px-3 py-2">
                <FileIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-sm text-foreground">{file.name}</span>
                    <span className="shrink-0 text-xs text-muted-foreground">{formatBytes(file.size)}</span>
                  </div>
                  {status === 'uploading' && <Progress value={progress} className="mt-1.5 h-1" />}
                  {status === 'error' && state?.error && (
                    <p className="mt-0.5 text-xs text-destructive">{state.error}</p>
                  )}
                </div>
                {status === 'done' && <Check className="h-4 w-4 shrink-0 text-primary" />}
                {status === 'error' && onRetry && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    onClick={(e) => {
                      e.stopPropagation()
                      onRetry(file)
                    }}
                    aria-label={`Retry ${file.name}`}>
                    <RefreshCw className="h-3.5 w-3.5" />
                  </Button>
                )}
                {(status === undefined || status === 'pending' || status === 'error') && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    onClick={(e) => {
                      e.stopPropagation()
                      removeFile(file)
                    }}
                    aria-label={`Remove ${file.name}`}>
                    <X className="h-3.5 w-3.5" />
                  </Button>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

export { FileInput }
